import { z } from 'zod';
import { endpoint } from './endpoint.js';
import {
  ImportJobSchema,
  ImportUploadResponseSchema,
  ImportStartResponseSchema,
  ImportErrorsResponseSchema,
  ImportValidateResponseSchema,
  ImportColumnsResponseSchema,
  ConfirmMappingResponseSchema,
  SuggestSuppliersQuerySchema,
  SuggestSuppliersResponseSchema,
  SaveSupplierResolutionsRequestSchema,
  SaveSupplierResolutionsResponseSchema,
} from '../schemas/import-job.js';
import { PhotoBundleUploadResponseSchema } from '../schemas/bulk-photo-import.js';

/** Queue name constant shared with worker. */
export const MEDIA_UNIT_IMPORT_QUEUE_NAME = 'media-unit-import';

export const importEndpoints = {
  /** POST /api/import/upload — upload XLSX, validate headers, create import_jobs record */
  upload: endpoint('POST', '/api/import/upload', z.object({}), ImportUploadResponseSchema),

  /** POST /api/import/:id/start — enqueue MediaUnitImportJob */
  start: endpoint(
    'POST',
    '/api/import/:id/start',
    z.object({ id: z.number() }),
    ImportStartResponseSchema,
  ),

  /** GET /api/import/:id — poll status + progress */
  getStatus: endpoint('GET', '/api/import/:id', z.object({ id: z.number() }), ImportJobSchema),

  /** GET /api/import/:id/errors — per-row error log */
  getErrors: endpoint(
    'GET',
    '/api/import/:id/errors',
    z.object({ id: z.number() }),
    ImportErrorsResponseSchema,
  ),

  /** GET /api/import/template — redirect to R2 pre-signed URL for template XLSX */
  getTemplate: endpoint('GET', '/api/import/template', z.object({}), z.object({})),

  /** POST /api/import/:id/validate — dry-run validation without persisting */
  validate: endpoint(
    'POST',
    '/api/import/:id/validate',
    z.object({ id: z.number() }),
    ImportValidateResponseSchema,
  ),

  /** GET /api/import/:id/columns — get detected columns + suggested mapping + suppliers */
  getColumns: endpoint(
    'GET',
    '/api/import/:id/columns',
    z.object({ id: z.coerce.number() }),
    ImportColumnsResponseSchema,
  ),

  /** POST /api/import/:id/confirm-mapping — confirm column mapping and transition to pending */
  confirmMapping: endpoint(
    'POST',
    '/api/import/:id/confirm-mapping',
    z.object({ id: z.coerce.number() }),
    ConfirmMappingResponseSchema,
  ),

  /** GET /api/import/suppliers/suggest?q=... — fuzzy-match supplier names */
  suggestSuppliers: endpoint(
    'GET',
    '/api/import/suppliers/suggest',
    SuggestSuppliersQuerySchema,
    SuggestSuppliersResponseSchema,
  ),

  /** POST /api/import/:id/supplier-resolutions — save resolved supplier name mappings */
  saveSupplierResolutions: endpoint(
    'POST',
    '/api/import/:id/supplier-resolutions',
    SaveSupplierResolutionsRequestSchema.extend({ id: z.number() }),
    SaveSupplierResolutionsResponseSchema,
  ),
};

/** POST /api/imports/photo-bundle — upload ZIP bundle and create bulk photo import job */
export const photoBundleUpload = endpoint(
  'POST',
  '/api/imports/photo-bundle',
  z.object({}),
  PhotoBundleUploadResponseSchema,
);
